"use client";

import { c as _c } from "react/compiler-runtime";
import { UserCircle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Field } from "@/src/components/cards/common";
import EditableCard from "@/src/components/cards/editable-card";
import FormField from "@/src/components/form-field";
import { Button } from "@/src/components/shadcn/ui/button";
import GeocodeLookupSection from "@/src/components/tasks/intake-sections/geocode-lookup-section";
import { toast } from "@/src/hooks/use-toast";
import { useCreateOrUpdatePatientMutation } from "@/src/lib/data-fetchers/patients-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { convertEmptyStringValuesToNull, getNestedValue } from "@/src/lib/utils/object-utils";
import { compareStrings, getDisplayValue } from "@/src/lib/utils/string-utils";
import { type Address } from "@/src/models/address";
import { Patient, PatientErrors, PatientFieldKey, PatientType, validatePatient } from "@/src/models/patient";
import { PersonEthnicity, PersonGender, PersonLanguage } from "@/src/models/person";

// Enhanced FormField component with conflict detection
interface FormFieldWithConflictProps {
  fieldKey: string;
  hasConflict: (fieldKey: string) => boolean;
  getSecondaryValue: (fieldKey: string) => string | null;
  handleInputChange: (field: PatientFieldKey, value: string) => void;
}
const FormFieldWithConflict = t0 => {
  const $ = _c(23);
  let fieldKey;
  let getSecondaryValue;
  let handleInputChange;
  let hasConflict;
  let props;
  if ($[0] !== t0) {
    ({
      fieldKey,
      hasConflict,
      getSecondaryValue,
      handleInputChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = fieldKey;
    $[2] = getSecondaryValue;
    $[3] = handleInputChange;
    $[4] = hasConflict;
    $[5] = props;
  } else {
    fieldKey = $[1];
    getSecondaryValue = $[2];
    handleInputChange = $[3];
    hasConflict = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== fieldKey || $[7] !== hasConflict) {
    t1 = hasConflict(fieldKey);
    $[6] = fieldKey;
    $[7] = hasConflict;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const conflict = t1;
  let t2;
  if ($[9] !== fieldKey || $[10] !== getSecondaryValue) {
    t2 = getSecondaryValue(fieldKey);
    $[9] = fieldKey;
    $[10] = getSecondaryValue;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const secondaryValue = t2;
  const t3 = conflict ? "bg-warn/10 border-warn/30" : "";
  let t4;
  if ($[12] !== props || $[13] !== t3) {
    t4 = <FormField {...props} className={t3} />;
    $[12] = props;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== conflict || $[16] !== fieldKey || $[17] !== handleInputChange || $[18] !== secondaryValue) {
    t5 = conflict && secondaryValue && <div className="flex items-center justify-between text-xs text-muted-foreground"><span className="text-warn">EMR: {secondaryValue}</span><Button variant="ghost" size="sm" type="button" className="h-6 text-xs px-2 py-0 hover:bg-muted rounded-full flex items-center justify-center" onClick={e => {
        e.preventDefault();
        handleInputChange(fieldKey as PatientFieldKey, secondaryValue);
      }}>Switch<RefreshCw className="ml-1 h-3.5 w-3.5" /></Button></div>;
    $[15] = conflict;
    $[16] = fieldKey;
    $[17] = handleInputChange;
    $[18] = secondaryValue;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t4 || $[21] !== t5) {
    t6 = <div className="space-y-1">{t4}{t5}</div>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};

// List of fields to validate for this card
const patientFieldsToValidate: (keyof Patient)[] = ["givenName", "middleName", "familyName", "ethnicity", "race", "language", "dateOfBirth", "gender", "homePhoneNumber", "mobilePhoneNumber", "personalEmail"];
const fieldsToValidate: PatientFieldKey[] = [...patientFieldsToValidate];
interface PatientCardEditableProps {
  patient: Patient;
  onClose: () => void;
  // Called after a successful create with the persisted patient (which carries
  // the server-assigned displayId). Falls back to onClose when not provided.
  onCreated?: (patient: Patient) => void;
  createMode?: boolean;
  compareMode?: boolean;
  secondaryObject?: Patient | null;
  fields?: Field[];
}
export default function PatientCardEditable({
  patient,
  onClose,
  onCreated,
  createMode = false,
  compareMode = false,
  secondaryObject = null,
  fields = []
}: PatientCardEditableProps) {
  const [formData, setFormData] = useState<Patient>({
    ...patient
  });
  const [errors, setErrors] = useState<PatientErrors>({});

  // Helper function to check if field has conflict with secondary object
  const hasConflict = (fieldKey: string) => {
    if (!compareMode || !secondaryObject) return false;
    const currentValue = getNestedValue(formData, fieldKey);
    const secondaryValue = getNestedValue(secondaryObject, fieldKey);
    const field = fields.find((f: Field) => f.key === fieldKey);
    const fieldType = field?.type ?? "text";
    return !compareStrings(currentValue || "", secondaryValue || "", fieldType);
  };

  // Helper function to get secondary value for switch functionality
  const getSecondaryValue = (fieldKey_0: string) => {
    if (!compareMode || !secondaryObject) return null;
    const val = getNestedValue(secondaryObject, fieldKey_0);
    const field_0 = fields.find((f_0: Field) => f_0.key === fieldKey_0);
    const fieldType_0 = field_0?.type ?? "text";
    return getDisplayValue(val, fieldType_0);
  };

  // Helper function to get field props based on field configuration
  const getFieldProps = (field_1: Field) => {
    const baseProps = {
      label: field_1.label,
      value: getNestedValue(formData, field_1.key) || "",
      onChange: (value: string) => handleInputChange(field_1.key as PatientFieldKey, value),
      error: getNestedValue(errors, field_1.key),
      required: field_1.required
    };

    // Add type-specific props
    switch (field_1.key) {
      case "dateOfBirth":
        return {
          ...baseProps,
          type: "date" as const
        };
      case "homePhoneNumber":
      case "mobilePhoneNumber":
        return {
          ...baseProps,
          type: "tel" as const,
          placeholder: "555-123-4567"
        };
      case "personalEmail":
        return {
          ...baseProps,
          type: "email" as const
        };
      case "gender":
        return {
          ...baseProps,
          type: "select" as const,
          options: Object.values(PersonGender).map(gender => ({
            value: gender,
            label: gender
          }))
        };
      case "ethnicity":
        return {
          ...baseProps,
          type: "select" as const,
          options: Object.values(PersonEthnicity).map(ethnicity => ({
            value: ethnicity,
            label: ethnicity
          }))
        };
      case "language":
        return {
          ...baseProps,
          type: "select" as const,
          options: Object.values(PersonLanguage).map(language => ({
            value: language,
            label: language
          }))
        };
      case "homeAddress?.line2":
        return {
          ...baseProps,
          placeholder: "Apt, Suite, etc."
        };
      case "type":
        return {
          ...baseProps,
          type: "select" as const,
          options: Object.values(PatientType).map(type => ({
            value: type,
            label: type
          }))
        };
      default:
        return baseProps;
    }
  };
  const createOrUpdatePatientMutation = useCreateOrUpdatePatientMutation();
  const handleAddressUpdate = (_key: string, value_0: Address | null) => {
    setFormData(prev => ({
      ...prev,
      homeAddress: value_0
    }));
    if (errors.homeAddress) {
      setErrors(prev_0 => ({
        ...prev_0,
        homeAddress: undefined
      }));
    }
  };
  const handleInputChange = (field_2: PatientFieldKey, value_1: string) => {
    const patientField = field_2 as keyof Patient;
    setFormData(prev_1 => ({
      ...prev_1,
      [patientField]: value_1
    }));

    // Clear error for this field when user starts typing
    setErrors(prev_2 => {
      const {
        [patientField]: _,
        ...rest
      } = prev_2;
      return rest;
    });
  };
  const handleSave = async () => {
    const processedFormData = convertEmptyStringValuesToNull(formData);
    const {
      isValid,
      errors: errors_0
    } = validatePatient(processedFormData, fieldsToValidate);

    // A patient needs at least a name to be meaningful; the schema treats these
    // as nullable, so enforce presence here.
    if (!processedFormData.givenName) errors_0.givenName = "Given Name is required";
    if (!processedFormData.familyName) errors_0.familyName = "Family Name is required";
    setErrors(errors_0);
    if (!isValid || errors_0.givenName || errors_0.familyName) {
      return;
    }
    try {
      const savedPatient = await createOrUpdatePatientMutation.mutateAsync(processedFormData);
      if (createMode && onCreated) {
        onCreated(savedPatient);
      } else {
        onClose?.();
      }
      toast({
        title: createMode ? "Patient created" : "Patient updated",
        description: createMode ? "The patient was successfully created." : "The patient's information was successfully saved.",
        variant: "default"
      });
    } catch (error) {
      logger.error("Error saving patient:", {
        error
      });
    }
  };
  const addressFieldKeys = ["homeAddress.line1", "homeAddress.line2", "homeAddress.city", "homeAddress.state", "homeAddress.postalCode"];
  const nonAddressFields = fields.filter(field_3 => !addressFieldKeys.includes(field_3.key));
  const hasAddressFields = fields.some(field_4 => addressFieldKeys.includes(field_4.key));
  return <EditableCard title={createMode ? "Create New Patient" : "Edit Patient Information"} icon={UserCircle} initialData={patient} onCancel={onClose} onSave={handleSave} isSaving={createOrUpdatePatientMutation.isPending} createMode={createMode}>
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-x-4 gap-y-4 text-sm">
          {nonAddressFields.map(field_5 => {
          const fieldProps = getFieldProps(field_5);
          const isComparable = field_5.comparable && compareMode;
          return <div key={field_5.key} className={field_5.colSpan === 2 ? "col-span-2" : ""}>
                {isComparable ? <FormFieldWithConflict fieldKey={field_5.key} hasConflict={hasConflict} getSecondaryValue={getSecondaryValue} handleInputChange={handleInputChange} {...fieldProps} /> : <FormField {...fieldProps} />}
              </div>;
        })}
        </div>
        {hasAddressFields && <GeocodeLookupSection address={formData.homeAddress} onUpdate={handleAddressUpdate} />}
      </div>
    </EditableCard>;
}